import React, {
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useContext,
} from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssistantIcon from '@mui/icons-material/AssistantOutlined';
import MailIcon from '@mui/icons-material/MailOutlineRounded';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import clsx from 'clsx';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import compact from 'lodash/compact';
import {
  NPISearchItemDTO,
  ProviderContextDTO,
  ServiceFacilityDTO,
  PatientDTO,
  UserDTO,
  EncounterNotificationDTO,
} from 'dtos';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  Link,
} from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatDateTimeOffset,
} from 'utils/date';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  useNotify,
  useMedia,
} from 'core/hooks';
import Controller from 'core/components/FormController';
import CalendarIcon from 'core/icons/calendar';
import ClockIcon from 'core/icons/clock';
import Tag from 'core/components/Tag';
import Editable from 'core/components/Editable';
import ProviderLookup from 'views/EMR/components/ProviderLookup';
import {
  daysOfWeek,
} from 'core/constants';
import Input from 'pages/Dashboard/components/Input';
import UserName from 'pages/Dashboard/components/UserName';
import Row from 'pages/Dashboard/components/Row';
import Select from 'core/components/Select';
import Spinner from 'pages/Dashboard/components/Spinner';
import TelehealthVisitTypeIcon from 'pages/Dashboard/components/TelehealthVisitTypeIcon';
import ToolTip from 'core/components/Tooltip';
import {
  useColorsHashMap,
  useEncounterSettings,
} from 'pages/Dashboard/pages/Encounters/hooks';
import PatientCommunication
  from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/PatientCommunication';
import AppointmentPreVisitStatusRows, {
  AppointmentConsentStatusRow,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/AppointmentPreVisitStatusRows';
import {
  getFallbackAppointmentIntakeStatus,
} from 'pages/Dashboard/utils/helper';
import {
  appointmentsEventChannel,
  billingEventChannel,
  insurancesEventChannel,
  paymentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  CalendarEvent,
  Recurrence,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  DateTimeIntervalValue,
  fromStartAndDuration,
} from 'core/components/DateTimePicker/dateTimeInterval';
import {
  useDeleteAppointment,
  useGetAppointmentById,
  useGetPatientPaymentTransactionsByEncounterId,
  useModifyAppointment,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import PhoneLink from 'pages/Dashboard/components/PhoneLink';
import ServiceFacilitySelect from 'pages/Dashboard/components/ServiceFacilitySelect';
import {
  handleAppointmentActionDialog,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/CreateAppointmentDialog';
import {
  useUpsertInsurances,
} from 'views/EMR/components/Insurance/hooks';
import ReferringProvider from 'pages/Dashboard/components/ReferringProvider';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import RecurringEditControls from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/RecurringEditControls';
import {
  eventTypeTitles,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  EventPanelInfo,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/eventPanelUrl';
import DeleteConfirmation, {
  DeleteConfirmationParams,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/DeleteConfirmation';
import Checkout from 'pages/Dashboard/components/Checkout';
import Section from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/Section';
import Toolbar from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/Toolbar';
import PanelExtension from 'core/components/PanelExtension';
import AppointmentStatusSelect from 'pages/Dashboard/components/AppointmentStatusSelect';
import useSignalREffect from 'views/EMR/hooks/signalR';
import EditableAppointmentInsurance from 'views/EMR/components/Insurance/EditableAppointmentInsurance';
import PrinterIcon from 'core/icons/printer';
import EditIcon from 'core/icons/edit';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import DateEditPanel from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/DateEditPanel';
import CallAIButton from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/CallAIButton';
import ViewProvider, {
  ViewContext,
} from 'pages/Dashboard/contexts/viewContext';
import PatientHeader from 'pages/Dashboard/components/PatientHeader';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import NotesIcon from 'core/icons/notes';
import PaymentRemoveIcon from 'core/icons/paymentRemove';
import PaymentSuccessIcon from 'core/icons/paymentSuccess';
import SummaryTotalItem from 'views/EMR/components/SummaryTotalItem';
import {
  formatMoney,
  isNilOrZero,
} from '@populateui/utils';
import StatementIcon from 'core/icons/statement';
import Receipts from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/Receipts';
import {
  useSelectedEntitiesActions,
} from 'views/EMR/stores/entities';
import RecurranceControl from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/RecurranceControl';
import PatientInsuranceEligibility from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/PatientInsuranceList';
import SendTempoLinkButton from 'pages/Dashboard/pages/Charts/pages/Details/components/Demographics/SendTempoLinkButton';
import Button from 'pages/Dashboard/components/Button';
import PatientAlerts from 'views/EMR/PatientAlerts';
import mapNPISearchItemToProviderEntry from 'pages/Dashboard/utils/providers';
import {
  useAppointmentReferringProviderSuggestions,
} from 'pages/Dashboard/components/ReferringProvider/hooks';

export type AppointmentForm = {
  eventDateTime: DateTimeIntervalValue;
  doctor: UserDTO;
  reason: string;
  typeOfVisitDescription: string;
  serviceFacility?: ServiceFacilityDTO;
  canBeRescheduledSooner?: boolean;
  referringProvider?: ProviderContextDTO;
  recurrence?: Recurrence;
};

type DetailsEntity = {
  id: number;
  isTimeBlock?: boolean;
  isAvailability?: boolean;
  isOpened?: boolean;
  // Occurrence start; when missing, the fetched event's start is used
  eventDate?: Dayjs;
};

const inputLabelClasses = 'text-gray-500 text-xs';
const commonSelectMuiClasses = { select: '!py-[3px] rounded' };

function DateTimeRange({
  formattedDateRange,
  formattedTimeRange,
}: Partial<CalendarEvent>) {
  return (
    <>
      <div className="flex items-center">
        <CalendarIcon className="!text-lg !text-gray-500" />
        <p className="nw-appointment-date-range ml-1">
          {formattedDateRange}
        </p>
      </div>
      <div className="flex items-center">
        <ClockIcon className="!w-4 !h-4 !text-gray-500" />
        <p className="nw-appointment-time-range ml-1">
          {formattedTimeRange}
        </p>
      </div>
    </>
  );
}

type TotalItemProps = {
  value?: number | null;
  title: string;
};

function TotalItem({ value: _value = 0, title }: TotalItemProps) {
  const value = _value ?? 0;
  return (
    <SummaryTotalItem
      title={title}
      value={value}
      Icon={value > 0 ? PaymentRemoveIcon : PaymentSuccessIcon}
      classes={{
        title: 'text-gray-500',
        icon: clsx({
          'bg-populate-green-1': value === 0,
          'bg-red-600': value > 0,
        }),
        value: clsx({
          'text-populate-green-1': value === 0,
          'text-red-600': value > 0,
        }),
      }}
    />
  );
}

function DetailsOutlet({
  entity: {
    id = 0,
    isTimeBlock = false,
    isOpened = false,
    isAvailability = false,
    eventDate,
  } = { id: 0 },
  onClose: _onClose,
  slots,
}: SidePanelComponentProps<DetailsEntity>) {
  const isAppointment = !isTimeBlock && !isAvailability;
  const [openReceipts, setOpenReceipts] = useState(false);
  const reasonControlRef = useRef<HTMLInputElement>(null);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [deleteOption, setDeleteOption] = useState<string>('current');
  const { extensionExpanded, Extension, setExtensionExpanded } = useContext(ViewContext);
  const [
    deleteConfirmationParams,
    setDeleteConfirmationParams,
  ] = useState<DeleteConfirmationParams | null>(null);
  const notify = useNotify();
  const [isDateEditOpen, setIsDateEditOpen] = useState(false);
  const isEnabled = id > 0 && isOpened;
  const reasonForVisitRef = useRef<HTMLParagraphElement>(null) as RefObject<HTMLParagraphElement>;
  const {
    data,
    isLoading,
    refetch,
    isFetching,
    isError,
    fetchStatus,
  } = useGetAppointmentById(id, isTimeBlock, isAvailability, isEnabled, { throwOnNoAccess: true });
  const {
    data: patientPayments = [],
    refetch: refetchPatientPayments,
    isLoading: loadingPatientPayments,
  } = useGetPatientPaymentTransactionsByEncounterId(id);

  const { requestMedia } = useMedia();
  const doctorInputRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;
  const { data: doctorsList = [] } = useGetUsers();

  const doctor = useMemo(() => (
    doctorsList.find((doctor) => doctor.userId === data?.userId)
  ), [doctorsList, data?.userId]);

  const {
    patient,
    objectId,
    reason,
    typeOfVisitDescription,
    patientAppointmentStatus,
    formattedTimeRange,
    formattedDateRange,
    confirmAppointmentCommunique,
    insuranceId,
    start,
    end,
    typeName,
    currentAppointmentDue,
    copayPaid = 0,
    isOutOfPocket,
    createdByUser,
    createdOn,
    isShiftedToPatient,
    consentStatus,
  } = data ?? {};

  const patientId = patient?.patientId ?? 0;
  const insurances = patient?.insurances ?? [];
  const selectedInsurance = useMemo(() => insurances.find(
    ({ insuranceId: id }) => insuranceId === id,
  ), [data]);

  const formProps = useForm<AppointmentForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      eventDateTime: fromStartAndDuration(data?.start, data?.durationInMinutes),
      doctor: doctor ?? {},
      reason: data?.reason ?? '',
      typeOfVisitDescription: data?.typeOfVisitDescription ?? '',
      serviceFacility: data?.serviceFacility,
      canBeRescheduledSooner: data?.canBeRescheduledSooner ?? false,
      referringProvider: data?.referringProvider ?? undefined,
      recurrence: {
        ...(data?.recurrence ?? {}),
        repeatType: { id: data?.recurrence?.repeat },
        repeatsOn: undefined,
      },
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getFieldState,
    formState,
    watch,
  } = formProps;

  const { isSubmitting, isValid } = formState;
  const repeatType = watch('recurrence.repeatType');
  const serviceFacilityNickname = watch('serviceFacility.nickname');
  const eventDateTime = watch('eventDateTime');
  const selectedDoctor = watch('doctor');

  const onClose = () => {
    reset();
    _onClose?.();
  };

  const {
    mutateAsync: deleteEvent,
    isPending: isDeletingAppointment,
  } = useDeleteAppointment();

  const { mutateAsync: updateInsurances } = useUpsertInsurances();
  const { mutateAsync: updateAppointment } = useModifyAppointment();
  const {
    appointmentTypes,
    isTelehealthByType,
  } = useEncounterSettings();

  const intakeStatus = getFallbackAppointmentIntakeStatus(confirmAppointmentCommunique);
  const consentDisplayStatus = consentStatus ?? 'NotAvailable';

  const isNoteFinalized = /note finalized/i.test(patientAppointmentStatus ?? '');
  const colorsHashMap = useColorsHashMap();
  const visitTypeColor = useMemo(() => (
    colorsHashMap[typeOfVisitDescription ?? ''] ?? 'bg-emerald-200'
  ), [typeOfVisitDescription, colorsHashMap]);

  const isTelehealthEnabledForSelectedDoctor =
    selectedDoctor?.isTelehealthEnabled ?? false;

  const getTypeOfVisitLabel = useCallback((option: string) => (
    <div className="flex items-center gap-1">
      {option}
      <TelehealthVisitTypeIcon
        isTelehealthVisitType={isTelehealthByType[option] ?? false}
        isTelehealthEnabledForProvider={isTelehealthEnabledForSelectedDoctor}
      />
    </div>
  ), [isTelehealthByType, isTelehealthEnabledForSelectedDoctor]);

  const serviceFacilityColor = useMemo(() => (
    colorsHashMap[serviceFacilityNickname ?? ''] ?? '!bg-emerald-700'
  ), [serviceFacilityNickname, colorsHashMap]);

  const eventTypeTitle = eventTypeTitles[typeName ?? 'appointment'];
  const isRecurringEvent = isAvailability || isTimeBlock;

  const onAppointmentModified = async () => {
    await refetch();
    appointmentsEventChannel.emit('onEventUpdated', id, true);
  };

  const onSave = async ({
    doctor,
    eventDateTime,
    reason,
    typeOfVisitDescription,
    serviceFacility,
    canBeRescheduledSooner,
    referringProvider,
    recurrence,
  }: AppointmentForm) => {
    if (isValid) {
      try {
        const appointment = await updateAppointment({
          ...data!,
          referringProvider,
          canBeRescheduledSooner,
          reason,
          userId: doctor.userId,
          start: eventDateTime.startTime,
          durationInMinutes: eventDateTime.durationInMinutes,
          typeOfVisitDescription,
          serviceFacility,
          ...(isRecurringEvent ? {
            recurrence: {
              ...recurrence,
              repeatType: recurrence?.repeatType ?? undefined,
              endsOn: recurrence?.endsOn ?? undefined,
              interval: recurrence?.interval ?? undefined,
              count: recurrence?.count ?? undefined,
              repeatsOn: recurrence?.repeatsOn ?? [],
            },
          } : {}),
        });

        if (!isNil(appointment)) {
          const successMessage = `${eventTypeTitle} updated`;
          handleAppointmentActionDialog(appointment, notify, successMessage);

          onAppointmentModified();
        }
      } catch (e: unknown) {
        console.error(e);
        reset({}, { keepIsValid: true });
      }
    }
  };

  const {
    suggestions: providerSuggestionData,
  } = useAppointmentReferringProviderSuggestions(
    patient?.patientId,
    id,
  );
  const referringProviderSuggestions = useMemo(() => (
    providerSuggestionData.map((suggestion) => ({
      ...suggestion,
      onSelect: () => {
        setValue('referringProvider', { providerEntry: suggestion.providerEntry });
        handleSubmit(onSave)();
      },
    }))
  ), [handleSubmit, onSave, providerSuggestionData, setValue]);

  const onConfirmDelete = async (
    event: CalendarEvent,
    date?: Dayjs,
    deleteOptionValue?: string,
  ) => {
    const isRecurring = !isNil(event?.recurrence);
    const isDeleteAll = deleteOptionValue === 'all' || !isRecurring;
    await deleteEvent({
      data: event!,
      ...(isRecurring ? {
        options: {
          deleteSingleItem: isDeleteAll
            ? undefined : deleteOptionValue === 'current',
          eventDate: formatDateTimeOffset(
            (isDeleteAll ? event?.eventDateRange?.[0] : date) ?? '',
          ),
        },
      } : {}),
    });
    appointmentsEventChannel.emit('onEventDeleted', id);
    notify({
      message: `${eventTypeTitle} deleted.`,
      variant: 'success',
    });
    setDeleteOption('current');
    setDeleteConfirmationParams(null);
  };

  const formData = useMemo(() => (!isNil(data) && !isNil(doctor)
    ? {
      eventDateTime: fromStartAndDuration(data?.start, data?.durationInMinutes),
      reason: data?.reason ?? '',
      isOutOfPocket: data?.isOutOfPocket ?? false,
      insurance: selectedInsurance,
      doctor,
      typeOfVisitDescription: data?.typeOfVisitDescription ?? '',
      serviceFacility: data?.serviceFacility,
      canBeRescheduledSooner: data?.canBeRescheduledSooner ?? false,
      referringProvider: data?.referringProvider ?? undefined,
      recurrence: {
        ...omit(data?.recurrence ?? {}, ['repeatType', 'repeatsOn']),
        repeatType: !isNil(data?.recurrence?.repeat)
          ? { id: data?.recurrence?.repeat ?? '', label: '' }
          : undefined,
        endsOn: !isNil(data?.recurrence?.endsOn)
          ? formatDate(data?.recurrence?.endsOn ?? '', false) : undefined,
        repeatsOn: !isNil(data?.recurrence?.repeatsOn)
          ? data?.recurrence?.repeatsOn?.map((day) => daysOfWeek[Number(day)])
          : undefined,
      },
    } : {}), [data, doctor]);

  // entity changed
  useEffect(() => {
    if (id > 0) {
      setIsDateEditOpen(false);
      setExtensionExpanded?.(null); // reset
      reset({});
      refetch();
    }
  }, [id]);

  // eventChannel
  useEffect(() => {
    reset();
    const unsubscribeFromUpdate = appointmentsEventChannel.on('onEventUpdated', async (encounterId) => {
      if (id === encounterId && id > 0) {
        await refetch();
      }
    });

    const unsubscribeFromDelete = appointmentsEventChannel.on('onEventDeleted', (encounterId) => {
      if (id === encounterId) {
        onClose();
      }
    });

    const unsubscribePatientPayment = paymentsEventChannel.on('onPaymentsDataChanged', (type) => {
      if (type === 'patient-payments') {
        refetchPatientPayments?.();
      }
    });

    const unsubscribePatientBalance = paymentsEventChannel.on('onPatientBalanceChange', async () => {
      if (id > 0) {
        await refetch();
      }
    });

    return () => {
      unsubscribeFromUpdate();
      unsubscribeFromDelete();
      unsubscribePatientPayment();
      unsubscribePatientBalance();
    };
  }, [id]);

  useSignalREffect('OnEncounterUpdated', async (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    if (Number(id) === encounterId && id > 0) {
      await refetch();
    }
  });

  useEffect(() => {
    if (id > 0 && !isEmpty(formData) && !isSubmitting && !isFetching) {
      const {
        eventDateTime,
        reason,
        doctor,
        typeOfVisitDescription,
        serviceFacility,
        canBeRescheduledSooner,
        referringProvider,
        recurrence,
      } = formData!;
      reset({
        ...(formData ?? {}),
        eventDateTime,
        reason,
        doctor,
        typeOfVisitDescription,
        serviceFacility,
        canBeRescheduledSooner,
        referringProvider,
        recurrence,
      }, { keepIsValid: true, keepDirty: false });
    }
  }, [id, formData, isSubmitting]);

  useEffect(() => {
    requestMedia([patientId]);
    const userIds = compact([doctor?.userId, createdByUser?.userId]);
    if (userIds.length > 0) {
      requestMedia(userIds, MediaOriginatorType.User);
    }
  }, [patientId, doctor?.userId, createdByUser?.userId, requestMedia]);

  const { setEntity, unsetEntity } = useSelectedEntitiesActions();
  const appointmentPatientEntityId = `appointment-${id}-patient`;

  useEffect(() => {
    if (!isNil(patient)) {
      setEntity(patient!, appointmentPatientEntityId);
    }
    return () => {
      unsetEntity(appointmentPatientEntityId);
    };
  }, [appointmentPatientEntityId, patient, setEntity, unsetEntity]);

  useEffect(() => {
    if (isAppointment && id > 0 && !isNil(data)) {
      setEntity({
        encounterId: id,
        reason: data.reason,
        doctor,
        serviceFacility: data.serviceFacility,
      }, id, 'appointment');
    }
    return () => {
      unsetEntity(id, 'appointment');
    };
  }, [id, isAppointment, data, patient, doctor, data?.reason]);

  const { isDirty: reasonHasChanged } = getFieldState('reason', formState);
  const { isDirty: doctorHasChanged } = getFieldState('doctor', formState);
  const { isDirty: visitTypeHasChanged } = getFieldState('typeOfVisitDescription', formState);
  const { isDirty: serviceFacilityHasChanged } = getFieldState('serviceFacility', formState);
  const { isDirty: dateTimeHasChanged } = getFieldState('eventDateTime', formState);
  const { isDirty: referringProviderChanged } = getFieldState('referringProvider', formState);

  const statusDisabled = /note finalized/i.test(patientAppointmentStatus ?? '');

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onInsuranceCorrection', async (insurance, patientId) => {
      if (patientId === patient?.patientId) {
        await updateInsurances({
          patientId,
          insurance,
        });
        await refetch();

        notify({
          message: 'Insurance Updated Successfully',
          variant: 'success',
        });
      }
    });
    return () => { unsubscribe(); };
  }, [id, patient, insurances]);

  useEffect(() => {
    const unsubscribe = insurancesEventChannel.on('onEligibilityCheckRefresh', async () => {
      await refetch();
    });
    return () => { unsubscribe(); };
  }, [id, patient, insurances]);

  const reminderStatus = useMemo(() => (
    isNil(confirmAppointmentCommunique)
      ? 'Not yet sent'
      : `Last sent at ${formatDateTime(confirmAppointmentCommunique?.lastSentOn ?? '')}`
  ), [confirmAppointmentCommunique]);

  useEffect(() => {
    if ((patient?.pinnedStickyCount ?? 0) > 0) {
      setExtensionExpanded?.('stickies');
    } else if (!extensionExpanded) {
      setExtensionExpanded?.(null);
    }
  }, [patient]);

  const userInfo = useUserInfo();

  const hasPatientPayment = useMemo(() => (patientPayments?.length ?? 0) > 0, [patientPayments]);
  const isEditable = !isNoteFinalized && !isSubmitting;

  return (
    <>
      <div className="flex min-w-[40rem] h-full">
        {!isLoading && !isNil(data) && !isError && id > 0 && (
          <>
            <div className={clsx('flex flex-col rounded-sm h-full !w-[40rem] overflow-hidden overflow-y-auto', {
              'justify-between': !isTimeBlock,
            })}
            >
              <FormProvider {...formProps}>
                <div className="nw-appointment-details flex flex-col h-full">
                  <div className={clsx('w-full z-[10] border-b flex', {
                    'items-center': isAvailability || isTimeBlock,
                  })}
                  >
                    {!isNil(slots?.header) && (
                      <div className="py-4 pl-4 !h-8">
                        {slots?.header}
                      </div>
                    )}
                    {isTimeBlock || isAvailability
                      ? (
                        <div className="items-center w-full justify-between flex p-4">
                          <UserName
                            user={doctor}
                            className="text-base flex items-center"
                          />
                        </div>
                      ) : (
                        <div className="flex-1 overflow-hidden">
                          <PatientHeader
                            className={clsx('border-none', { '-ml-4': !isNil(slots?.header) })}
                            patient={(patient ?? {}) as PatientDTO}
                            isLoading={isLoading}
                            infoProps={{ size: 'md' }}
                            classes={{
                              nameContainer: '!items-start !h-10',
                            }}
                          />
                        </div>
                      )}
                    <Toolbar
                      eventDate={eventDate ?? data.start}
                      data={data}
                      isNoteFinalized={isNoteFinalized}
                      setDeleteConfirmationParams={setDeleteConfirmationParams}
                      onClose={onClose}
                      actions={slots?.actions}
                      isDeleting={isDeletingAppointment}
                    />
                  </div>
                  <div className="overflow-auto flex flex-col h-full">
                    <div className="z-[10] p-4 flex flex-col gap-2">
                      {!isAvailability && (
                      <div className="text-sm">
                        <Editable
                          controlRef={reasonControlRef as RefObject<HTMLInputElement>}
                          idleView={(
                            <div className="w-min">
                              <div className="text-sm items-center space-x-1 flex w-full">
                                {!isNil(reason) && (
                                <ToolTip
                                  title={reason}
                                  placement="bottom-start"
                                  auto
                                  ref={reasonForVisitRef}
                                >
                                  <p
                                    className="nw-appointments-reason-for-visit flex-auto max-w-[360px] truncate mr-4"
                                    ref={reasonForVisitRef}
                                  >
                                    {reason}
                                  </p>
                                </ToolTip>
                                )}
                              </div>
                            </div>
                          )}
                          tooltip={`Edit ${isTimeBlock ? 'Time Block' : 'Appointment'} Reason`}
                          isLoading={isSubmitting && reasonHasChanged}
                          readonly={isSubmitting || isNoteFinalized}
                          onSubmit={handleSubmit(onSave)}
                          classes={{ editableRoot: '!text-black', iconRoot: 'nw-edit-reason-button' }}
                        >
                          {(onSubmit, onCancel) => (
                            <Controller
                              name="reason"
                              control={control}
                              rules={{ required: true }}
                              render={({
                                field,
                                fieldState: { error },
                              }) => (
                                <Input
                                  {...field}
                                  id="event-reason"
                                  className="w-full bg-white min-w-[25rem]"
                                  inputRef={reasonControlRef}
                                  error={!isNil(error)}
                                  showErrorText={false}
                                  onBlur={() => {
                                    if (reasonHasChanged) {
                                      onSubmit();
                                    } else {
                                      onCancel();
                                    }
                                  }}
                                  onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    if (e.key === 'Escape' || (e.key === 'Enter' && !reasonHasChanged)) {
                                      onCancel();
                                    } else if (e.key === 'Enter') {
                                      onSubmit();
                                    }
                                  }}
                                />
                              )}
                            />
                          )}
                        </Editable>
                      </div>
                      )}
                      <div className="flex justify-between gap-2">
                        <div className="flex flex-col gap-2">
                          <div className="flex gap-3 items-center">
                            {isTimeBlock && (
                              <Tag
                                tagName="Time Blocked"
                                hasStripes
                              />
                            )}
                            {isAvailability && (
                              <Controller
                                name="serviceFacility"
                                control={control}
                                render={({ field }) => (
                                  <ServiceFacilitySelect
                                    {...field}
                                    isLoading={isSubmitting && serviceFacilityHasChanged}
                                    className="!w-fit"
                                    muiClasses={{
                                      select: clsx(
                                        commonSelectMuiClasses.select,
                                        `!text-white ${serviceFacilityColor}`,
                                        {
                                          '!bg-gray-100 !cursor-wait': isSubmitting && serviceFacilityHasChanged,
                                        },
                                      ),
                                      icon: '!text-white',
                                    }}
                                    onChange={(value: ServiceFacilityDTO) => {
                                      field.onChange(value);
                                      handleSubmit(onSave)();
                                    }}
                                  />
                                )}
                              />
                            )}
                            {isAppointment && (
                              <>
                                <Link
                                  to={`/encounters/${data?.objectId}`}
                                  className="!w-max border rounded !border-blue-500 font-semibold !text-xs !h-6 py-0.5 px-1 flex items-center text-blue-500 gap-2"
                                >
                                  <NotesIcon className="!h-4 !w-4 !text-inherit" />
                                  Visit Note
                                </Link>
                                <Controller
                                  control={control}
                                  name="typeOfVisitDescription"
                                  render={({ field }) => (
                                    <Select<string>
                                      {...field}
                                      muiClasses={{
                                        select: clsx(
                                          commonSelectMuiClasses.select,
                                          '!font-semibold',
                                          {
                                            '!bg-gray-100 !cursor-wait': isSubmitting && visitTypeHasChanged,
                                            [visitTypeColor]: !visitTypeHasChanged,
                                            '!text-white': /500$/i.test(visitTypeColor),
                                          },
                                        ),
                                      }}
                                      selectClassName={clsx('!border-0 !pointer-events-auto')}
                                      id="type-of-visit"
                                      className="text-gray-500 text-xs"
                                      placeholder={typeOfVisitDescription ?? 'Select'}
                                      options={appointmentTypes}
                                      getLabel={(option: string) => getTypeOfVisitLabel(option)}
                                      getValue={(option: string) => option}
                                      onChange={(option) => {
                                        field.onChange(option as string);
                                        handleSubmit(onSave)();
                                      }}
                                      disabled={isSubmitting || isNoteFinalized}
                                      renderValue={(val: string) => (
                                        <p className="max-w-[160px] truncate">
                                          {isSubmitting && visitTypeHasChanged ? (
                                            <>
                                              <Spinner className="mr-2" />
                                              Updating...
                                            </>
                                          ) : val}
                                        </p>
                                      )}
                                    />
                                  )}
                                />
                                <div className="flex items-center">
                                  <AppointmentStatusSelect
                                    encounterId={id}
                                    value={patientAppointmentStatus ?? ''}
                                    appointmentDate={eventDateTime?.startTime}
                                    disabled={statusDisabled}
                                    onUpdate={onAppointmentModified}
                                    classes={{
                                      root: 'flex-1',
                                      label: inputLabelClasses,
                                      select: commonSelectMuiClasses?.select,
                                    }}
                                  />
                                </div>
                              </>
                            )}
                          </div>
                          <div className="flex gap-3 items-center text-xs">
                            <div
                              className={clsx('flex items-center gap-1 rounded p-1', {
                                'cursor-default': !isEditable,
                                'hover:shadow hover:shadow-gray-200 hover:bg-white cursor-pointer': isEditable,
                              })}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && isEditable) {
                                  setIsDateEditOpen(true);
                                }
                              }}
                              onClick={() => {
                                if (isEditable) {
                                  setIsDateEditOpen(true);
                                }
                              }}
                            >
                              {isSubmitting && dateTimeHasChanged ? (
                                <span className="text-gray-500">Updating...</span>
                              ) : (
                                <>
                                  <DateTimeRange
                                    formattedDateRange={formattedDateRange ?? '--'}
                                    formattedTimeRange={formattedTimeRange ?? '--'}
                                  />
                                  {!isNoteFinalized && !isSubmitting && (
                                    <EditIcon className="!w-5 !h-5 !text-gray-700 nw-edit-appointment-date-time-range-button" />
                                  )}
                                </>
                              )}
                            </div>
                            {isDateEditOpen && (
                              <DateEditPanel
                                open
                                onClose={() => setIsDateEditOpen(false)}
                                onSubmit={() => {
                                  handleSubmit(onSave)();
                                  setIsDateEditOpen(false);
                                }}
                                isAppointment={isAppointment}
                                isTimeBlock={isTimeBlock}
                                data={data as CalendarEvent}
                                dateInputRef={dateInputRef as RefObject<HTMLInputElement>}
                              />
                            )}
                          </div>
                          {isAppointment && (
                            <Row
                              title=""
                              delimiter={null}
                              classes={{
                                root: '!justify-start !p-0 border-b-0',
                                title: '!w-0',
                              }}
                            >
                              <Controller
                                name="canBeRescheduledSooner"
                                control={control}
                                render={({ field }) => (
                                  <Checkbox
                                    {...field}
                                    onChange={(value) => {
                                      field.onChange(Boolean(value));
                                      handleSubmit(onSave)();
                                    }}
                                    id="can-be-scheduled-sooner"
                                    checked={Boolean(field.value)}
                                    value={(field?.value ?? '').toString()}
                                    label="Patient would rather be seen sooner if schedule permits"
                                  />
                                )}
                              />
                            </Row>
                          )}
                          {isRecurringEvent && (
                            <Row
                              title=""
                              classes={{
                                root: '!justify-start !p-0',
                                title: '!w-0',
                              }}
                              delimiter={null}
                            >
                              <RecurranceControl
                                onSave={onSave}
                                data={formData}
                                label={isTimeBlock ? 'time block' : 'availability'}
                              />
                            </Row>
                          )}
                        </div>
                      </div>
                      {isAppointment && (
                        <div className="w-full gap-4 flex items-center mt-4">
                          <TotalItem
                            title="Current appointment due"
                            value={data?.currentAppointmentDue}
                          />
                          <TotalItem
                            title="Total balance overdue"
                            value={data?.totalDue}
                          />
                          <SummaryTotalItem
                            title="Patient's Credit"
                            value={data?.credit ?? 0}
                            Icon={PaymentSuccessIcon}
                            classes={{
                              title: 'text-gray-500',
                              icon: 'bg-populate-green-1',
                              value: 'text-populate-green-1',
                            }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex w-full flex-col">
                      {(isRecurringEvent && !isNil(repeatType)) && (
                        <RecurringEditControls
                          onSave={onSave}
                          data={formData}
                        />
                      )}
                      {isAppointment && patientId > 0 && id > 0 && (
                        <div className="px-4 pb-2">
                          <PatientAlerts
                            patientId={patientId}
                            encounterId={id}
                            size="small"
                          />
                        </div>
                      )}
                      {isAppointment && (
                      <div className="flex flex-col gap-2 px-4 pb-4">
                        <Section title="General Information">
                          <Row title="Doctor">
                            <Controller
                              name="doctor"
                              control={control}
                              render={({ field }) => {
                                const selectedDoctor = field?.value ?? doctor;

                                return (
                                  <Editable
                                    readonly={isSubmitting || isNoteFinalized}
                                    classes={{ editableRoot: 'max-w-[50%]' }}
                                    idleView={(
                                      <UserName
                                        user={selectedDoctor}
                                        className="font-semibold"
                                      />
                                    )}
                                    showActions={false}
                                    isLoading={isSubmitting && doctorHasChanged}
                                    controlRef={doctorInputRef}
                                    onSubmit={handleSubmit(onSave)}
                                    tooltip="Change Doctor"
                                  >
                                    {(onSubmit, onCancel) => (
                                      <ProviderLookup
                                        {...field}
                                        inputRef={doctorInputRef}
                                        blurOnSelect
                                        onClose={onCancel}
                                        disabled={isLoading}
                                        onChange={(val: UserDTO | UserDTO[]) => {
                                          const nextValue = Array.isArray(val) ? val[0] : val;
                                          field.onChange(nextValue ?? null);
                                          onSubmit();
                                        }}
                                      />
                                    )}
                                  </Editable>
                                );
                              }}
                            />
                          </Row>
                          <Row title="Facility">
                            <Controller
                              name="serviceFacility"
                              control={control}
                              render={({ field }) => (
                                <ServiceFacilitySelect
                                  {...field}
                                  isLoading={isSubmitting && serviceFacilityHasChanged}
                                  muiClasses={{
                                    select: clsx(
                                      commonSelectMuiClasses.select,
                                      { '!bg-gray-100 !cursor-wait': isSubmitting },
                                    ),
                                  }}
                                  onChange={(val: ServiceFacilityDTO) => {
                                    field.onChange(val);
                                    handleSubmit(onSave)();
                                  }}
                                />
                              )}
                            />
                          </Row>
                          <Row
                            title="Referring Provider"
                            className="!items-start"
                            classes={{ title: 'w-1/2' }}
                          >
                            <Controller
                              control={control}
                              name="referringProvider"
                              render={({ field }) => (
                                <div className="justify-end">
                                  <ReferringProvider
                                    {...field}
                                    value={field.value?.providerEntry}
                                    onChange={(value?: NPISearchItemDTO | null) => {
                                      field.onChange(isNil(value) ? null : {
                                        providerEntry: mapNPISearchItemToProviderEntry(value),
                                        firstSeenOn: field.value?.firstSeenOn ?? null,
                                        lastSeenOn: field.value?.lastSeenOn ?? null,
                                      });
                                      handleSubmit(onSave)();
                                    }}
                                    suggestions={referringProviderSuggestions}
                                    dateSeen={{
                                      firstSeenOn: field.value?.firstSeenOn ?? null,
                                      lastSeenOn: field.value?.lastSeenOn ?? null,
                                      onChange: ({ firstSeenOn, lastSeenOn }) => {
                                        field.onChange({
                                          ...(field.value ?? {}),
                                          firstSeenOn: firstSeenOn ?? null,
                                          lastSeenOn: lastSeenOn ?? null,
                                        });
                                        handleSubmit(onSave)();
                                      },
                                    }}
                                    variant="compact"
                                    showContactButton={false}
                                    classes={{
                                      name: 'font-semibold',
                                      button: clsx('!w-full', { '!h-12': !isNil(field.value?.providerEntry) }),
                                      valueContainer: 'flex-col !items-start !gap-0',
                                    }}
                                    hideBackdrop={false}
                                    loading={referringProviderChanged && isLoading}
                                  />
                                </div>
                              )}
                            />
                          </Row>
                          <Row title="Patient's Primary Phone">
                            <PhoneLink
                              phoneNumberWithAreaCode={patient?.phoneNumber?.phoneNumberWithAreaCode ?? ''}
                            />
                          </Row>
                          {!isNil(patient?.otherPhoneNumber) && (
                            <Row title="Patient's Other Phone">
                              <PhoneLink
                                phoneNumberWithAreaCode={patient?.otherPhoneNumber?.phoneNumberWithAreaCode ?? ''}
                              />
                            </Row>
                          )}
                          <Row title="Created by">
                            <UserName
                              user={createdByUser}
                              className="!max-w-1/2 !w-fit font-semibold"
                            />
                          </Row>
                          <Row title="Created at">
                            {formatDateTime(createdOn ?? '', false)}
                          </Row>
                        </Section>
                        <Section title="Insurance">
                          <Row title="Insurance">
                            <EditableAppointmentInsurance
                              patientId={patient?.patientId ?? 0}
                              encounterId={objectId}
                              isOutOfPocket={isOutOfPocket}
                              insuranceId={insuranceId ?? 0}
                              shiftedToPatient={isShiftedToPatient ?? false}
                            />
                          </Row>
                          <Row title="Patient Insurance Eligibility">
                            <PatientInsuranceEligibility
                              patient={patient}
                              startDate={start?.toISOString()}
                              endDate={end?.toISOString()}
                              selectedInsuranceId={selectedInsurance?.insuranceId ?? 0}
                              disabled={isFetching}
                              isLoading={isFetching}
                              encounterId={objectId ?? 0}
                            />
                          </Row>
                        </Section>
                        <Section title={(
                          <div className="flex w-full gap-2 items-center">
                            Payment
                            {(!isOutOfPocket && copayPaid > 0) && (
                              <>
                                <Divider
                                  flexItem
                                  orientation="vertical"
                                />
                                <div className="text-xs flex items-center gap-1 text-populate-green-1 font-semibold">
                                  <CheckCircleIcon className="!w-4 !h-4 !text-inherit cursor-pointer" />
                                  Copay collected
                                  {': '}
                                  {formatMoney(copayPaid ?? 0)}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                        >
                          <Row title="Appointment Due">
                            <Checkout
                              patient={patient}
                              due={currentAppointmentDue}
                              encounter={{
                                encounterId: objectId,
                                encounterDate: formatDateTimeOffset(eventDate ?? data.start),
                              }}
                            />
                          </Row>
                          <Row title="Patient's Credit">
                            <div className="items-center flex justify-end gap-2">
                              <Button
                                className="!border-secondary disabled:!border-gray-300 flex gap-1 !px-2.5 !py-0.5 !min-w-[120px]"
                                variant="outlined"
                                color="primary"
                                disabled={isNilOrZero(patient?.patientId)}
                                onClick={() => {
                                  billingEventChannel.emit('onEncountersAllocation', { patient: patient! });
                                }}
                              >
                                Allocate
                              </Button>
                            </div>
                          </Row>
                          <Row title="Statement & Receipts">
                            <div className="flex gap-2">
                              <ToolTip title="Print Patient Statement">
                                <span>
                                  <IconButton
                                    aria-label="Print Patient Statement"
                                    color="info"
                                    size="small"
                                    onClick={() => {
                                      paymentsEventChannel.emit('onStatementDialog', {
                                        type: 'print',
                                        patient: patient as Patient,
                                        visits: [objectId ?? 0],
                                      });
                                    }}
                                    className="disabled:opacity-50"
                                  >
                                    <PrinterIcon className="!w-4 !h-4 !text-inherit" />
                                  </IconButton>
                                </span>
                              </ToolTip>
                              <ToolTip
                                title="Send Patient Statement"
                              >
                                <span>
                                  <IconButton
                                    aria-label="Send Patient Statement"
                                    color="info"
                                    size="small"
                                    onClick={() => {
                                      paymentsEventChannel.emit('onStatementDialog', {
                                        type: 'send',
                                        patient: patient as Patient,
                                        visits: [objectId ?? 0],
                                      });
                                    }}
                                    className="disabled:opacity-50"
                                  >
                                    <MailIcon className="!w-4 !h-4 !text-inherit" />
                                  </IconButton>
                                </span>
                              </ToolTip>
                              <Divider orientation="vertical" variant="middle" flexItem />
                              <ToolTip title={hasPatientPayment ? 'Open Receipts' : 'No Receipts'}>
                                <span>
                                  <IconButton
                                    aria-label="Open Receipts"
                                    color="info"
                                    size="small"
                                    onClick={() => {
                                      setOpenReceipts(true);
                                    }}
                                    disabled={loadingPatientPayments || !hasPatientPayment}
                                    className="disabled:opacity-50"
                                  >
                                    <StatementIcon className={clsx('!w-4 !h-4', { 'cursor-not-allowed !text-gray-400': !hasPatientPayment })} />
                                  </IconButton>
                                </span>
                              </ToolTip>
                            </div>
                            {hasPatientPayment && openReceipts && (
                              <Receipts
                                data={patientPayments}
                                onClose={() => { setOpenReceipts(false); }}
                              />
                            )}
                          </Row>
                        </Section>
                        <Section
                          title={(
                            <div className="py-1">
                              <div className="text-sm">Intake &amp; consent</div>
                              <div className="text-xs font-normal text-gray-500">{reminderStatus}</div>
                            </div>
                          )}
                          headerActions={<PatientCommunication appointment={data} />}
                        >
                          <AppointmentPreVisitStatusRows
                            intakeStatus={intakeStatus}
                          />
                          <Row
                            title="Consent Forms"
                            className="border-t border-b-gray-300"
                          >
                            <AppointmentConsentStatusRow
                              consentStatus={consentDisplayStatus}
                            />
                          </Row>
                          {userInfo?.flags?.maySendTempoCommunique && (
                            <Row
                              title="Tempo"
                              className="!items-start"
                            >
                              <div className="flex gap-2 items-center">
                                <SendTempoLinkButton
                                  patientId={patientId ?? 0}
                                  encounterId={objectId ?? 0}
                                />
                              </div>
                            </Row>
                          )}
                          {userInfo?.flags?.agents?.appointmentReminderAgentEnabled && (
                            <Row
                              className="!items-center"
                              delimiter=""
                              title={(
                                <div className="flex gap-1 items-center text-blue-500">
                                  <AssistantIcon className="!w-4 !h-4 !text-inherit" />
                                  AI Appointment Confirmation
                                </div>
                              )}
                            >
                              <CallAIButton encounterId={objectId ?? 0} />
                            </Row>
                          )}
                        </Section>
                      </div>
                      )}
                    </div>
                  </div>
                </div>
              </FormProvider>
            </div>
            {!isNil(patient) && (
              <PanelExtension className={clsx({ '!w-[25rem]': extensionExpanded })}>
                {!isNil(Extension) && (
                  <Extension
                    patient={patient as PatientDTO}
                    className="bg-gray-100"
                    classes={{ list: 'px-4' }}
                    onClose={() => { setExtensionExpanded?.(null); }}
                  />
                )}
              </PanelExtension>
            )}
          </>
        )}
        {id > 0 && (isError || fetchStatus === 'paused' || isNil(data)) && (
          <div className="flex flex-col justify-center items-center m-4 p-4 rounded-sm !w-full h-full">
            {!isError && fetchStatus !== 'paused' && <Spinner className="!h-10 !w-10" />}
            {(isError || fetchStatus === 'paused') && (
              <div className="flex flex-col items-center gap-3 text-center">
                <p className="text-sm text-gray-500">
                  {fetchStatus === 'paused'
                    ? 'No internet connection. Please check your connection and try again.'
                    : 'Something went wrong. Please try again.'}
                </p>
                <Button
                  variant="outlined"
                  onClick={() => {
                    refetch();
                  }}
                  className="disabled:opacity-50"
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      {!isNil(deleteConfirmationParams) && (
        <DeleteConfirmation
          {...(deleteConfirmationParams ?? {})}
          deleteOptionValue={deleteOption}
          onDeleteOptionChange={(e, v) => { setDeleteOption(v); }}
          onConfirm={onConfirmDelete}
          onClose={() => { setDeleteConfirmationParams(null); }}
        />
      )}
    </>
  );
}

export default function AppointmentDetails({
  entity: { eventId, kind, occurrence },
  ...props
}: SidePanelComponentProps<EventPanelInfo>) {
  const entity = useMemo<DetailsEntity>(() => ({
    id: eventId,
    isTimeBlock: kind === 'timeBlock',
    isAvailability: kind === 'availability',
    isOpened: true,
    eventDate: isNil(occurrence) ? undefined : dayjs(occurrence),
  }), [eventId, kind, occurrence]);

  return (
    <ViewProvider isPanel>
      <DetailsOutlet {...props} entity={entity} />
    </ViewProvider>
  );
}
